import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const ADMIN_EMAIL = String(
  process.env.REACT_APP_ADMIN_EMAILS || process.env.REACT_APP_ADMIN_EMAIL || ""
)
  .split(",")[0]
  .trim()
  .toLowerCase();
const ADMIN_ROUTE = process.env.REACT_APP_ADMIN_ROUTE || "/notifications";
const REQUEST_NOTIFY_FUNCTION =
  process.env.REACT_APP_REQUEST_NOTIFY_FUNCTION || "notify-access-request";
const ADMIN_LOGIN_PENDING_KEY = "portfolio_admin_login_pending";
const ADMIN_LOGIN_CONFIG_KEY = "portfolio_admin_login_config";
const ADMIN_POST_LOGIN_KEY = "portfolio_admin_post_login";
const ADMIN_CALLBACK_PATH = "/admin-auth-v2.html";
const ADMIN_POST_LOGIN_WINDOW_MS = 60000;

export const supabase =
  SUPABASE_URL && SUPABASE_KEY
    ? createClient(SUPABASE_URL, SUPABASE_KEY, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      })
    : null;

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const persistAdminLoginConfig = () => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(
    ADMIN_LOGIN_CONFIG_KEY,
    JSON.stringify({
      supabaseUrl: SUPABASE_URL,
      supabaseKey: SUPABASE_KEY,
      adminEmail: ADMIN_EMAIL,
      adminRoute: ADMIN_ROUTE,
    })
  );
};

const markAdminLoginPending = () => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ADMIN_LOGIN_PENDING_KEY, "true");
};

export const clearAdminLoginPending = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(ADMIN_LOGIN_PENDING_KEY);
};

export const hasPendingAdminLogin = () =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(ADMIN_LOGIN_PENDING_KEY) === "true";

export const hasAdminAuthCallback = () =>
  typeof window !== "undefined" &&
  window.location.search.includes("code=");

const markAdminPostLogin = (email) => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(
    ADMIN_POST_LOGIN_KEY,
    JSON.stringify({
      email: normalizeEmail(email),
      timestamp: Date.now(),
    })
  );
};

const clearAdminPostLogin = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(ADMIN_POST_LOGIN_KEY);
};

const hasRecentAdminPostLogin = () => {
  if (typeof window === "undefined") return false;

  try {
    const rawValue = window.localStorage.getItem(ADMIN_POST_LOGIN_KEY);
    if (!rawValue) return false;

    const parsed = JSON.parse(rawValue);
    const timestamp = Number(parsed?.timestamp || 0);
    const email = normalizeEmail(parsed?.email);

    return (
      email === ADMIN_EMAIL &&
      timestamp > 0 &&
      Date.now() - timestamp <= ADMIN_POST_LOGIN_WINDOW_MS
    );
  } catch {
    return false;
  }
};

const clearAuthSearch = () => {
  if (typeof window === "undefined" || !window.location.search) return;
  const nextUrl = `${window.location.pathname}${window.location.hash || ""}`;
  window.history.replaceState({}, document.title, nextUrl);
};

const getSessionUserWithRetry = async (timeoutMs, intervalMs = 300) => {
  if (!supabase) return null;

  const effectiveTimeoutMs =
    typeof timeoutMs === "number"
      ? timeoutMs
      : hasRecentAdminPostLogin()
        ? 30000
        : 15000;

  const readSessionUser = async () => {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error && error.message !== "Auth session missing!") {
      console.error("Supabase session error:", error.message);
    }

    return session?.user || null;
  };

  return await new Promise((resolve) => {
    let settled = false;
    let polling = false;

    const finish = (user) => {
      if (settled) return;
      settled = true;
      clearInterval(poller);
      clearTimeout(timeout);
      subscription?.unsubscribe();
      resolve(user || null);
    };

    const poller = setInterval(async () => {
      if (polling || settled) return;
      polling = true;
      try {
        const user = await readSessionUser();
        if (user) {
          finish(user);
        }
      } finally {
        polling = false;
      }
    }, intervalMs);

    const timeout = setTimeout(() => {
      finish(null);
    }, effectiveTimeoutMs);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        finish(session.user);
      }
    });

    readSessionUser()
      .then((user) => {
        if (user) {
          finish(user);
        }
      })
      .catch(() => {
        // polling and auth-state listeners continue until timeout.
      });
  });
};

export const isAuthorizedAdminUser = (user) => {
  if (!user?.email || !ADMIN_EMAIL) return false;
  return normalizeEmail(user.email) === ADMIN_EMAIL;
};

export const triggerLogin = async () => {
  if (!supabase) {
    return { error: new Error("Supabase client is not configured") };
  }

  persistAdminLoginConfig();
  markAdminLoginPending();

  const redirectTo = `${window.location.origin}${ADMIN_CALLBACK_PATH}`;
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo,
      queryParams: {
        prompt: "select_account",
      },
    },
  });

  if (error) {
    clearAdminLoginPending();
    console.error("OAuth start failed:", error.message);
  }

  return { data, error };
};

export const handleAuthRedirect = async () => {
  if (!supabase) return false;

  const user = await getSessionUserWithRetry();

  if (isAuthorizedAdminUser(user)) {
    clearAdminLoginPending();
    clearAdminPostLogin();
    clearAuthSearch();
    window.location.replace(`${window.location.origin}/#${ADMIN_ROUTE}`);
    return true;
  }

  if (user) {
    clearAdminLoginPending();
    clearAuthSearch();
  }

  return false;
};

export const getAdminDebug = async () => {
  if (!supabase) {
    return {
      hasSupabase: false,
      adminEmail: ADMIN_EMAIL,
      sessionEmail: "",
      authorized: false,
      route: typeof window !== "undefined" ? window.location.href : "",
      error: "Supabase client is not configured",
      pendingAdminLogin: hasPendingAdminLogin(),
    };
  }

  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  const user = session?.user || null;

  return {
    hasSupabase: true,
    adminEmail: ADMIN_EMAIL,
    sessionEmail: user?.email || "",
    authorized: isAuthorizedAdminUser(user),
    route: typeof window !== "undefined" ? window.location.href : "",
    error: error?.message || "",
    pendingAdminLogin: hasPendingAdminLogin(),
  };
};

export const isAdminUser = async () => {
  const user = await getSessionUserWithRetry();
  return isAuthorizedAdminUser(user);
};

export const getAdminUser = async () => {
  const user = await getSessionUserWithRetry();

  if (isAuthorizedAdminUser(user)) {
    clearAdminLoginPending();
    clearAdminPostLogin();
    return user;
  }

  return null;
};

export const signOutAdmin = async () => {
  if (!supabase) return;
  clearAdminLoginPending();
  clearAdminPostLogin();
  await supabase.auth.signOut({ scope: "local" });
};

export const fetchRequests = async () => {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("access_requests")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error(error.message);
    return [];
  }

  return data || [];
};

export const notifyAccessRequest = async (payload) => {
  if (!supabase) {
    return { error: new Error("Supabase client is not configured") };
  }

  try {
    const { data, error } = await supabase.functions.invoke(
      REQUEST_NOTIFY_FUNCTION,
      {
        body: payload,
      }
    );

    if (error) {
      console.error("Request notification failed:", error.message);
    }

    return { data, error };
  } catch (error) {
    console.error("Request notification crashed:", error.message || error);
    return { error };
  }
};

export function getSupabase() {
  return supabase;
}
